import CheckIcon from 'assets/icons/step-done-check.svg?component'
import Alert from 'components/UI/Alert'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import Button from 'components/UI/Buttons/Button'
import RadioGroup from 'components/UI/RadioGroup'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import { useAppDispatch, useAppSelector } from 'hooks'
import { Fragment, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetScoringOkedsQuery } from 'redux/api/scoringApi'
import { okedIDSelector, programSelector, setOkedID } from 'redux/store/slices/decisionSlice'
import tw from 'twin.macro'

type LoanActivitySelectionProps = {
  isActivitySelected: boolean
  onIsActivitySelectedChange: (value: boolean) => void
}

const LoanActivitySelection = ({ isActivitySelected, onIsActivitySelectedChange }: LoanActivitySelectionProps) => {
  // Tools
  const {
    t,
    i18n: { language }
  } = useTranslation()
  const dispatch = useAppDispatch()

  // Selectors
  const selectedProgram = useAppSelector(programSelector)
  const oked = useAppSelector(okedIDSelector)

  // Query
  const { data: okeds, isLoading } = useGetScoringOkedsQuery()

  const filteredOkeds = useMemo(() => {
    if (!okeds || !selectedProgram) return []
    return okeds.filter(oked => oked.program_codes.includes(selectedProgram))
  }, [okeds, selectedProgram])

  // Handlers
  const handleOkenChange = (value: string) => {
    dispatch(setOkedID(value))
  }

  useEffect(() => {
    if (!okeds || !selectedProgram) return
    const okedsBySelectedProgram = okeds.filter(oked => oked.program_codes.includes(selectedProgram))
    const firstOked = okedsBySelectedProgram.at(0)
    if (!firstOked) return
    dispatch(setOkedID(firstOked.code))
  }, [dispatch, okeds, selectedProgram])

  if (isActivitySelected) {
    const selectedOked = filteredOkeds?.find(o => o.code === oked)
    return (
      <InfoBlock twStyle={tw`flex items-center gap-x-6`}>
        <div>
          <Typography variant='caption' twStyle={tw`text-grey-dark mb-2`}>
            {t('flow-activity-type')}
          </Typography>
          <Typography weight='bold'>
            {selectedOked?.code} - {language === 'ru' ? selectedOked?.description : selectedOked?.description_kk}
          </Typography>
          <Button variant='link' onClick={() => onIsActivitySelectedChange(false)}>
            {t('flow-change')}
          </Button>
        </div>
        <CheckIcon tw='flex-shrink-0 w-5 h-5 ml-auto' />
      </InfoBlock>
    )
  }

  return (
    <Fragment>
      <InfoBlock twStyle={tw`space-y-5 flex flex-col px-0 py-4 md:p-5`} isContentLoading={isLoading}>
        <Heading variant='subtitle' as='h2' weight='bold' twStyle={tw`px-5 md:px-0`}>
          {t('flow-choose-activity')}
        </Heading>
        <Alert twStyle={tw`mx-5 md:mx-0`}>
          <Typography variant='subbody' text>
            {t('flow-choose-activity2')}
          </Typography>
        </Alert>
        <RadioGroup
          value={oked}
          onChange={handleOkenChange}
          groupName='kind-of-activity'
          options={filteredOkeds?.map(oked => ({
            content: (
              <Fragment>
                <Typography variant='caption' twStyle={tw`text-grey-dark`}>
                  {oked.code}
                </Typography>
                <Typography variant='body' weight='bold'>
                  {language === 'ru' ? oked.description : oked.description_kk}
                </Typography>
              </Fragment>
            ),
            value: oked.code
          }))}
        />
        <Button
          disabled={!filteredOkeds || filteredOkeds.length === 0 || !oked}
          variant='secondary'
          size='small'
          twStyle={tw`self-end mr-5 md:mr-0`}
          onClick={() => onIsActivitySelectedChange(true)}
        >
          <Typography variant='caption' weight='bold'>
            {t('flow-choose')}
          </Typography>
        </Button>
      </InfoBlock>
    </Fragment>
  )
}

export default LoanActivitySelection
